<script>
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';
import CustomerProfileCompanyItems from '@/V2/customers/profile/CustomerProfileCompanyItems.vue';
import CustomerProfileIndividualsItems from '@/V2/customers/profile/CustomerProfileIndividualsItems.vue';
import CustomerComplianceCheckDetails from '@/V2/customers/business-compliance-agent/CustomerComplianceCheckDetails.vue';
import CustomerProfilePersonalDetails from '@/V2/customers/profile/CustomerProfilePersonalDetails.vue';
import { mapActions, mapGetters } from 'vuex';
import { iSearchInput, iSearchInputSizes } from '@inscribe/signal';

// create a map of company items with icon and label
const profileItemsConfig = {
  company_name: {
    label: 'Name',
    icon: 'medium-building-factory',
    path: 'company.legal_name',
  },
  company_description: {
    label: 'Description',
    icon: 'medium-text-4',
    path: 'company.business_description',
  },
  company_address: {
    label: 'Address',
    icon: 'medium-map-location-pin',
    path: 'company.address',
    value_field: 'full_address',
  },
  business_type: {
    label: 'Type',
    icon: 'medium-dashboard-5',
    path: 'company.business_type',
  },
  company_structure: {
    label: 'Company Structure',
    icon: 'medium_building',
    path: 'company.company_structure',
  },
  phone_number: {
    label: 'Phone Number',
    icon: 'medium-phone',
    path: 'company.phone_number',
  },
  ownership_structure: {
    label: 'Ownership Structure',
    icon: 'medium_network_6',
    path: 'company.ownership_structure',
    value_field: 'description',
  },
  source_of_funds_claim: {
    label: 'Source of Funds Claim',
    icon: 'medium-money-coins',
    path: 'company.source_of_funds_claim',
  },
  company_website: {
    label: 'Website',
    icon: 'medium-interface-link',
    path: 'company.website',
  },
  role: {
    label: 'Role',
    icon: 'medium_share_user',
    path: 'company.role',
  },
};

const individualsItemsConfig = {
  company_address: {
    label: 'Address',
    icon: 'medium-map-location-pin',
    path: 'address',
    value_field: 'full_address',
  },
  role: {
    label: 'Role',
    icon: 'medium_user_circle',
    path: 'role',
  },
};

const personalDetailsFields = [
  {
    label: 'Name',
    path: 'person_name.value',
    icon: 'medium_user_circle',
  },
  {
    label: 'City',
    path: 'person_address.city',
    icon: 'medium-map-location-pin',
  },
  {
    label: 'Country',
    path: 'person_address.country',
    icon: 'medium_map_earth_1',
  },
  {
    label: 'State',
    path: 'person_address.state',
    icon: 'small_travel_map',
  },
  {
    label: 'Post Code',
    path: 'person_address.post_code',
    icon: 'medium_mail_postbox',
  },
  {
    label: 'Full Address',
    path: 'person_address.full_address',
    icon: 'medium_map_compass',
  },
];

export default {
  components: {
    DocumentsTitleUpload,
    CustomerProfileCompanyItems,
    CustomerProfileIndividualsItems,
    CustomerComplianceCheckDetails,
    CustomerProfilePersonalDetails,
    iSearchInput,
  },
  data() {
    return {
      isDataLoading: false,
      profileItemsConfig,
      individualsItemsConfig,
      personalDetailsFields,
      searchQuery: '',
      detailItem: null,
      iSearchInputSizes,
    };
  },
  computed: {
    ...mapGetters(['getCustomerProfile']),
    customerProfile() {
      return this.getCustomerProfile(this.$route.params.customer_id);
    },
    hasPersonalDetails() {
      const personalDetailsFields = this.personalDetailsFields.filter(
        (field) => {
          const value = this.getItemFromPath(this.customerProfile, field.path);
          return (
            this.isDataEmpty(value) &&
            (field?.label
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase()) ||
              value?.toLowerCase().includes(this.searchQuery.toLowerCase()))
          );
        },
      );
      return personalDetailsFields.length > 0;
    },
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    ...mapActions(['fetchCustomerProfile']),
    async fetchInitialData() {
      this.isDataLoading = true;
      await this.fetchCustomerProfile(this.$route.params.customer_id).finally(
        () => {
          this.isDataLoading = false;
        },
      );
    },
    handleSearch(value) {
      this.searchQuery = value;
    },
    onItemClick(item) {
      this.detailItem = {
        ...item,
        title: item.label,
      };
    },
    onHideDrawer() {
      this.detailItem = null;
    },
    isDataEmpty(data) {
      return !!data && data !== '' && data.length > 0;
    },
    getItemFromPath(obj, path) {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    },
  },
};
</script>

<template>
  <div class="customer-profile-page i-p-11">
    <div class="customer-profile-container d-flex flex-column i-gap-11">
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Profile"
        subtitle="View the details of this customer"
        :expanded="false"
      >
      </documents-title-upload>

      <!-- search box -->
      <i-search-input
        v-model="searchQuery"
        input-placeholder="Search profile"
        class="i-w-full"
        :size="iSearchInputSizes.LARGE"
        @input="handleSearch"
      />

      <!-- business details-->
      <customer-profile-company-items
        :customer-profile="customerProfile"
        :is-loading="isDataLoading"
        :profile-items-config="profileItemsConfig"
        :search-query="searchQuery"
        :show-component-if-empty="!hasPersonalDetails"
        @on-item-click="onItemClick"
      />

      <!-- beneficiary details-->
      <customer-profile-individuals-items
        :customer-profile="customerProfile"
        :is-loading="isDataLoading"
        :individuals-items-config="individualsItemsConfig"
        :search-query="searchQuery"
        @on-item-click="onItemClick"
      />

      <!-- personal details -->
      <customer-profile-personal-details
        v-if="hasPersonalDetails"
        :customer-profile="customerProfile"
        :personal-details-fields="personalDetailsFields"
        :is-loading="isDataLoading"
        :search-query="searchQuery"
        @on-item-click="onItemClick"
      />
    </div>

    <!-- details drawer -->
    <customer-compliance-check-details
      :check="detailItem"
      @close="onHideDrawer"
    />
  </div>
</template>

<style scoped>
.customer-profile-page {
  overflow-y: auto;

  .customer-profile-container {
    max-width: 1200px;
    margin: 0 auto;

    :deep(.header) {
      margin-bottom: 0;
    }

    :deep(.expandable-item) {
      min-height: 68px;
    }
  }
}
</style>
